/**
 * Color utilities for components that cannot use Tailwind classes directly.
 * Used for: Leaflet map markers/popups, SweetAlert dialogs, inline HTML.
 *
 * IMPORTANT: Always prefer Tailwind classes when possible.
 * Use these utilities ONLY for inline HTML that cannot accept classes.
 */

/**
 * Get computed CSS variable value from current theme
 */
export function getCSSVarValue(varName: string): string {
  if (typeof window === 'undefined') return '';
  const root = document.documentElement;
  return getComputedStyle(root).getPropertyValue(varName).trim();
}

/**
 * Convert RGB triplet "R G B" to hex "#RRGGBB"
 */
function rgbTripletToHex(rgbTriplet: string): string {
  const parts = rgbTriplet.split(' ').map(Number);
  if (parts.length !== 3 || parts.some(isNaN)) {
    return '#666666'; // fallback
  }
  const [r, g, b] = parts;
  return `#${[r, g, b].map(c => Math.max(0, Math.min(255, c)).toString(16).padStart(2, '0')).join('')}`;
}

/**
 * Get hex color from CSS variable (handles RGB triplet format from index.css)
 */
export function getHexFromCSSVar(varName: string): string {
  const value = getCSSVarValue(varName);
  if (!value) return '#666666';
  // If already hex, return as-is
  if (value.startsWith('#')) return value;
  // Convert RGB triplet to hex
  return rgbTripletToHex(value);
}

/**
 * Status colors for inline HTML usage
 * These map to CSS variables defined in index.css
 */
export const statusColors = {
  success: () => getHexFromCSSVar('--status-success'),
  warning: () => getHexFromCSSVar('--status-warning'),
  error: () => getHexFromCSSVar('--status-error'),
  info: () => getHexFromCSSVar('--status-info'),
  neutral: () => getHexFromCSSVar('--status-neutral'),
};

/**
 * Location precision colors
 * Maps to --location-high, --location-medium, --location-low in index.css
 */
export const locationColors = {
  high: () => getHexFromCSSVar('--location-high'),
  medium: () => getHexFromCSSVar('--location-medium'),
  low: () => getHexFromCSSVar('--location-low'),
};

/**
 * Service brand colors
 * Maps to --service-google, --service-whatsapp, --service-telegram in index.css
 */
export const serviceColors = {
  google: () => getHexFromCSSVar('--service-google'),
  whatsapp: () => getHexFromCSSVar('--service-whatsapp'),
  telegram: () => getHexFromCSSVar('--service-telegram'),
};

/**
 * Get precision color based on GPS accuracy in meters
 */
export function getPrecisionColor(accuracyMeters?: number): string {
  if (!accuracyMeters) return statusColors.neutral();
  if (accuracyMeters <= 50) return locationColors.high();
  if (accuracyMeters <= 200) return locationColors.medium();
  return locationColors.low();
}

/**
 * Get precision level info based on GPS accuracy
 */
export function getPrecisionLevel(accuracyMeters?: number): { level: string; color: string } {
  if (!accuracyMeters) return { level: 'Unknown', color: statusColors.neutral() };
  if (accuracyMeters <= 50) return { level: 'High Precision', color: locationColors.high() };
  if (accuracyMeters <= 200) return { level: 'Medium Precision', color: locationColors.medium() };
  return { level: 'Low Precision', color: locationColors.low() };
}

/**
 * Text colors for popup content
 * Reads from CSS variables to respect current theme (light/dark)
 */
export const textColors = {
  primary: () => getCSSVarValue('--foreground'),
  secondary: () => getCSSVarValue('--muted-foreground'),
  border: () => getCSSVarValue('--border'),
  background: () => getCSSVarValue('--background'),
  muted: () => getCSSVarValue('--muted'),
};

/**
 * Get hex color for text (converts oklch to approximate hex)
 * For popup HTML that needs hex colors
 */
export function getTextHex(type: 'primary' | 'secondary' = 'primary'): string {
  // These are approximate hex values that match the oklch values in index.css
  // In light mode: primary ~= dark gray, secondary ~= medium gray
  // In dark mode: primary ~= light gray, secondary ~= medium gray
  const isDark = document.documentElement.classList.contains('dark');

  if (type === 'primary') {
    return isDark ? '#c9c5bf' : '#524d45'; // approximates --foreground
  }
  return isDark ? '#9a9590' : '#6b6560'; // approximates --muted-foreground
}

/**
 * Get background hex color for popups
 */
export function getBackgroundHex(): string {
  const isDark = document.documentElement.classList.contains('dark');
  return isDark ? '#3d3a36' : '#faf9f7'; // approximates --background
}

/**
 * Get border hex color for popups
 */
export function getBorderHex(): string {
  const isDark = document.documentElement.classList.contains('dark');
  return isDark ? '#4a4744' : '#e0dfdc'; // approximates --border
}
